import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { SuccessAlertContext } from "App";
import TableCellSmall, {
  MultiTypography,
  TableCellWide,
  TableHeaderCellSmall,
} from "./TableCellSmall";
import type { ManagedItem, CSVCountCheckOrder, CSVThresholdCheckStock } from "../types";

const limit = 10;

const styles: { [key: string]: SxProps } = {
  supplierTopCol: {
    textAlign: "center",
    p: 0.5,
    borderBottom: "none",
  },
  supplierBottomCol: {
    textAlign: "center",
    p: 0.5,
    top: 33,
  },
};

const tableHeadthreshold = (error?: string) => (
  <TableHead>
    <TableRow>
      <TableHeaderCellSmall rowSpan={2}>商品コード</TableHeaderCellSmall>
      <TableHeaderCellSmall rowSpan={2}>JANコード</TableHeaderCellSmall>
      <TableHeaderCellSmall rowSpan={2}>大分類</TableHeaderCellSmall>
      <TableHeaderCellSmall rowSpan={2}>仕入先名</TableHeaderCellSmall>
      <TableHeaderCellSmall rowSpan={2}>ブランド</TableHeaderCellSmall>
      <TableHeaderCellSmall rowSpan={2}>商品名</TableHeaderCellSmall>
      <TableHeaderCellSmall rowSpan={2}>サイズ</TableHeaderCellSmall>
      <TableHeaderCellSmall rowSpan={2}>ジョイフル仕切単価</TableHeaderCellSmall>
      <TableHeaderCellSmall rowSpan={2}>削除</TableHeaderCellSmall>
      {error && <TableHeaderCellSmall rowSpan={2}>エラー内容</TableHeaderCellSmall>}
    </TableRow>
  </TableHead>
);

const tableBodyThreshold = (s: CSVThresholdCheckStock, error?: string) => (
  <TableRow key={s.item_cd} sx={error && s.error ? { backgroundColor: "rgb(255,170,170)" } : {}}>
    <TableCellSmall>{s.item_cd}</TableCellSmall>
    <TableCellSmall>{s.jan_cd}</TableCellSmall>
    {s.symbolThresholdList.length >= 2 && (
      <>
        <TableCellSmall
          sx={{ textAlign: "right" }}
        >{`${s.symbolThresholdList[0].toLocaleString()}`}</TableCellSmall>
        <TableCellSmall
          sx={{ textAlign: "right" }}
        >{`${s.symbolThresholdList[1].toLocaleString()}`}</TableCellSmall>
      </>
    )}
    <TableCellSmall>
      <Checkbox
        // color="secondary"
        checked={s.isDiscontinued}
        disabled
      />
    </TableCellSmall>
    {error &&
      (s.error ? (
        <TableCellWide>
          <MultiTypography>{s.error}</MultiTypography>
        </TableCellWide>
      ) : (
        <TableCellWide />
      ))}
  </TableRow>
);

type Props = {
  open: boolean;
  title: string;
  content: string;
  ActionButtons: JSX.Element;
  productList: ManagedItem[];
  editedPrices: {item_cd: string, store_wholesale_price: number | null}[];
  stockList: (CSVCountCheckOrder | CSVThresholdCheckStock)[];
  isThreshold?: boolean;
  isFastLoading?: MutableRefObject<boolean>;
  getMoreStockList?: Function;
  isUpdating?: boolean;
  error?: string;
  errorTitle?: string;
  onClose: () => void;
  setInit?: Dispatch<SetStateAction<boolean>>;
};

/**
 * 在庫数量更新確認モーダルのテーブル
 * @param root0 コンポーネントのprops
 * @returns JSX.Element
 */
function UpdateConfirmTable({
  open,
  title,
  content,
  ActionButtons,
  productList,
  editedPrices,
  stockList,
  isThreshold,
  isFastLoading,
  getMoreStockList,
  isUpdating,
  error,
  errorTitle,
  onClose,
  setInit,
}: Props) {
  const [getMoreLoadEvent, setGetMoreLoadEvent] = useState(false);
  const [checked, setChecked] = useState(false);
  const [adjustment, setAdjustment] = useState<number | undefined>(undefined);
  const contentRef = useRef<HTMLDivElement | undefined>(undefined);
  const innerRef = useRef<HTMLDivElement | undefined>(undefined);

  const { showAlert } = useContext(SuccessAlertContext);

  const handleScroll = () => {
    if (isFastLoading?.current || !contentRef.current) {
      return;
    }
    const scrollTop = contentRef.current?.scrollTop;
    const contentHeight = contentRef.current?.clientHeight;
    const scrollHeight = contentRef.current?.scrollHeight;
    if (scrollTop + contentHeight >= scrollHeight) {
      setGetMoreLoadEvent((prev) => !prev);
    }
  };

  useEffect(() => {
    // addEventListenerするとstateの値を変更しても内部の値は変わらないため、stateを変更させるだけにし、ここで処理を行う。
    (async () => {
      if (getMoreStockList) await getMoreStockList();
    })();
  }, [getMoreLoadEvent]);
  useEffect(() => {
    if (setInit) {
      setInit(true);
      if (error) showAlert(error, "error");
    }
  }, [open]);

  useEffect(() => {
    if (stockList.length === limit && !isUpdating) {
      setTimeout(() => {
        if (
          contentRef.current &&
          innerRef.current &&
          contentRef.current.clientHeight - innerRef.current.clientHeight >= 0
        ) {
          // 中のコンテンツが小さい場合、調整オブジェクトを挿入
          setAdjustment(contentRef.current.clientHeight - innerRef.current.clientHeight + 1);
        } else {
          setAdjustment(undefined);
        }
      }, 0);
    } else {
      // setAdjustment(undefined);
    }
  }, [stockList, isUpdating]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent ref={contentRef} onScroll={handleScroll}>
        <Box ref={innerRef}>
          <DialogContentText>{error ? errorTitle : content}</DialogContentText>
          <TableContainer
            component={Paper}
            sx={{ overflowX: "initial", width: "fit-content", minWidth: "100%" }}
          >
            <Table sx={{ width: "100%", borderCollapse: "separate" }} aria-label="order table">
              {stockList.length !== 0 &&
                (isThreshold ? (
                  tableHeadthreshold(error)
                ) : (
                  <TableHead>
                    <TableRow>
                      <TableHeaderCellSmall>商品コード</TableHeaderCellSmall>
                      <TableHeaderCellSmall>JANコード</TableHeaderCellSmall>
                      <TableHeaderCellSmall>大分類</TableHeaderCellSmall>
                      <TableHeaderCellSmall>仕入先名</TableHeaderCellSmall>
                      <TableHeaderCellSmall>ブランド</TableHeaderCellSmall>
                      <TableHeaderCellSmall>商品名</TableHeaderCellSmall>
                      <TableHeaderCellSmall>サイズ</TableHeaderCellSmall>
                      <TableHeaderCellSmall>ジョイフル仕切単価</TableHeaderCellSmall>
                      <TableHeaderCellSmall>削除</TableHeaderCellSmall>
                      {error && <TableHeaderCellSmall>エラー内容</TableHeaderCellSmall>}
                    </TableRow>
                  </TableHead>
                ))}

              <TableBody>
                {productList.length === editedPrices.length && productList.map((it, idx) => (
                  // eslint-disable-next-line no-param-reassign
                  <TableRow key={it.item_cd}>
                    <TableCellSmall>{it.item_cd}</TableCellSmall>
                    <TableCellSmall>{it.jan_cd}</TableCellSmall>
                    <TableCellSmall>{it.class_name}</TableCellSmall>
                    <TableCellSmall>{it.vendor_name}</TableCellSmall>
                    <TableCellSmall>{it.brand}</TableCellSmall>
                    <TableCellSmall>{it.item_name}</TableCellSmall>
                    <TableCellSmall>{it.size}</TableCellSmall>
                    <TableCellSmall>{editedPrices[idx].store_wholesale_price}</TableCellSmall>
                    <TableCellSmall sx={{ flexDirection: "column" }}>
                      <Checkbox
                        // color="secondary"
                        required
                        checked={checked}
                        disabled
                      />
                    </TableCellSmall>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {adjustment && <Box height={adjustment} />}
          {isUpdating && (
            <Box py={3} textAlign="center">
              <CircularProgress />
            </Box>
          )}
        </Box>
      </DialogContent>
      {/* Button Area */}
      <DialogActions>{ActionButtons}</DialogActions>
    </Dialog>
  );
}

export default UpdateConfirmTable;
